import { formatNumberDisplayValue, maxDecimalValue } from '@/utils/numberUtil';
import { Translations } from '@/utils/translations/Translations';
export const englishTranslations: Translations = {
  admin: {
    applicationCyclesCurrent: 'Current Application Cycles',
    applicationCyclesHeader: 'Application Cycles',
    applicationCyclesPast: 'Past Application Cycles',
    applicationCyclesUpcoming: 'Upcoming Application Cycles',
    applicationsHeader: 'Applications',
    emailsHeader: 'Emails',
    tabEmailActivity: 'Email Activity',
    tabSendEmail: 'Send Email',
    tabTcowMassApproval: 'TCOW Mass Approval',
  },
  address: {
    addAddress2: '+ Apt/Suite/Unit',
    labelCity: 'City',
    labelCountry: 'Country',
    labelCounty: 'County',
    labelState: 'State',
    labelStreetAddress: 'Address',
    labelStreetAddress2: 'Apt/Suite/Unit',
    labelZipCode: 'Postal Code',
  },
  applicationBody: {
    approveApplicationButton: 'Approve Application',
    resubmitButton: 'Resubmit',
    sendBackToApplicantWithComments: 'Send Application Back to Applicant with Comments',
    starApprovalButton: 'Approve Star',
    starRemovalButton: 'Remove Star',
    submitButton: 'Submit',
    submitMessage: 'Submitting...',
    tooltipApproveSendsToNextReviewStage:
      'Clicking "Approve" sends the application to the next review stage.',
    tooltipApproveBeforeSubmit: 'Please ensure your review is complete before clicking "Approve."',
    tooltipApproveWithUnresolvedComments:
      'Please resolve all comments before approving the application. Unresolved comments can be found within standards that are yellow in the progress bar.',
    tooltipNextStandard: 'Next Standard',
    tooltipPreviousStandard: 'Previous Standard',
    tooltipRequiredQuestions: 'Please Complete All Required Questions First',
    tooltipSendBackRequiresComment:
      'Please enter a comment for this review stage before sending the application back to the applicant.',
  },
  applicationHeader: {
    headerViewingApplication: 'Viewing Application for ',
    messageRequiredQuestions: 'Please Complete All Required Questions',
    missingRequiredQuestions: 'Missing Required Questions',
    reviewerComments: 'Reviewer Comments',
    standardCompleted: 'Complete',
    starAwarded: 'Star Awarded',
    yearsRecognized: (year: number) => `Application for Year ${year} of Recognition`,
  },
  applicationPreview: {
    backToAdmin: 'Back to Admin',
    previewModeMessage: 'Preview mode is enabled. Changes in this view are not saved.',
    programLabel: 'Program',
    unknownValue: 'Unknown',
  },
  common: {
    ariaSaved: 'Saved',
    ariaSaveFailed: 'Save failed',
    ariaSaving: 'Saving',
    buttonPost: 'Post',
    buttonReactivate: 'Reactivate',
    buttonResolve: 'Resolve',
    defaultConfirmationModalText: {
      header: 'Confirm Action',
      confirmationMessage: 'Are you sure you want to proceed? This action cannot be undone.',
      confirmationButtonText: 'Confirm',
      cancelButtonText: 'Cancel',
      successMessage: 'Delete successful!',
    },
    buttonSaving: 'Saving...',
    buttonSave: 'Save',
    error: 'Error',
    buttonDelete: 'Delete',
    buttonUpdate: 'Update',
    characterCountRemaining: (remaining: number, max: number) =>
      `${remaining}/${max} characters left`,
    commentResolved: 'Comment Resolved',
    genericError: 'An error occurred. Please try again.',
    messageCommentAddedSuccessfully: 'Comment added successfully!',
    messageCommentAddFailed: 'Failed to add comment',
    messageCommentCannotBeEmpty: 'Comment cannot be empty!',
    messageCommentTooLong: 'Comment is too long!',
    messageGrowthAwardApprovalSuccess: 'Approval successful!',
    messageGrowthAwardApproveProblem:
      'There was a problem approving the Growth Award. Please try again later.',
    messageGrowthAwardApproving: 'Approving Growth Award ...',
    messageGrowthAwardCategoriesFetchError: 'Error fetching growth award categories.',
    messageGrowthAwardCommentRequired:
      'Please enter a comment before sending the application back to the applicant.',
    messageGrowthAwardReturnSuccess: 'Return successful!',
    messageGrowthAwardSendBackProblem: 'There was a problem sending the application back.',
    messageGrowthAwardSendingBack: 'Sending Growth Award Back to Applicant...',
    messageGrowthAwardSubmitted: 'Growth Award Application Submitted!',
    messageGrowthAwardSubmitting: 'Submitting Growth Award ...',
    messageGrowthAwardSubmitProblem: 'There was a problem submitting the Growth Award.',
    noMatch: 'No Match Found',
    placeholderPostComment: 'Post a Comment',
    previewInputPlaceholder: 'Preview input (not saved)',
    previewWorkflowSpecificDataNotice:
      'This question uses workflow-specific data in production. Preview mode captures local text only and does not call APIs.',
    buttonCancel: 'Cancel',
    buttonClose: 'Close',
    buttonContinue: 'Continue',
    requiredIndicatorTitle: 'required',
    statusApplicationApproved: 'Application Approved',
    statusApplicationReturned: 'Application Returned',
    statusApplicationSubmitted: 'Application Submitted',
    statusApproving: 'Approving...',
    statusReturning: 'Returning...',
    statusUpdating: 'Updating ...',
    tooltipSubmitGrowthAward: 'Click to "Submit" Growth Award Application.',
    tooltipRetrySave: 'Retry Save',
    tooltipSaveFailed: 'Save Failed',
  },
  contact: {
    confirmationModal: {
      header: 'Remove Contact',
      confirmationMessage:
        'Are you sure you want to remove this contact? This action cannot be undone.',
      confirmationButtonText: 'Remove Contact',
      successMessage: 'Contact removed successfully!',
    },
    buttonAdd: 'Add New Contact',
    buttonChange: 'Change Contact',
    buttonCreate: 'Select or Create New Contact',
    buttonSave: 'Save Contact',
    buttonUpdate: 'Update Contact',
    createContact: 'Create New Contact',
    editContact: 'Edit Contact',
    editWarning: (appYear: string) =>
      `You are about to modify the details of an existing contact. These changes will update everywhere this contact is used on ${appYear} applications.`,
    invalidEmail: 'Please enter a valid email address.',
    invalidZipCode: 'Please enter a valid postal code.',
    invalidPhoneNumber: 'Invalid phone number',
    requiredFieldSuffix: 'required',
    labelCity: 'City',
    labelCountry: 'Country',
    labelEmail: 'Email',
    labelFirstName: 'First Name',
    labelLastNameLabel: 'Last Name',
    labelName: 'Name',
    labelPhoneNumber: 'Phone Number',
    labelStreetAddress2Label: 'Street Address 2',
    labelStreetAddressLabel: 'Street Address',
    labelTitle: 'Title',
    labelWhatsApp: 'WhatsApp or Other Phone Number',
    labelZipCode: 'Postal Code',
    messageContactRemoved: 'Contact removed!',
    messageContactSaveFailed: 'Unable to save contact.',
    messageMaximum: "You've reached the maximum number of contacts for this section.",
    messageSaved: 'Contact Saved!',
    placeHolderPhoneNumber: 'Enter phone number',
    treeBoardLimitText: (maxInstances: number) =>
      `Please provide the names and emails of up to ${maxInstances} additional Tree Board members. Note that we do not require any additional information beyond ${maxInstances} additional members.`,
  },
  dashboard: {
    accessToolkit: 'Access Toolkit',
    accessToolkitPendingApproval: 'Available once your application is approved',
    download: 'Download',
    downloadTooltip: 'Download & Print Application',
    fieldApplicationCycle: 'Application Cycle',
    fieldApplication: 'Application',
    fieldStatus: 'Status',
    fieldSubmittedBy: 'Submitted By',
    growthAwardCardHeader: 'APPLY FOR GROWTH AWARD',
    growthAwardCardPendingLabel: (programName: string) =>
      `Growth Award Application can be started once your ${programName} application has been submitted.`,
    growthAwardCardStartButton: 'Start Growth Award Application',
    labelApplication: 'APPLICATION',
    labelCreateApplication: 'Create New Application',
    labelPastApplications: 'Applications',
    messageCreateApplication: 'Creating a new application ...',
    messageNoApplications: 'No Applications',
    portalClosedInformation: (latestAppCycleYear: string, nextOpenDate?: string | null) =>
      `The ${latestAppCycleYear != null ? latestAppCycleYear + ' ' : ''}application is currently closed. ` +
      (nextOpenDate != null ? `Applications will open on ${nextOpenDate}. ` : '') +
      'Thank you.',
    selectPartner: 'Select a Partner',
    stateQuestionsCardHeader: 'YOUR STATE REQUESTS ADDITIONAL INFORMATION',
    stateQuestionsCardPendingLabel:
      'State Specific Questions can be started once a Tree City USA application has been submitted.',
    stateQuestionsCardStartButton: 'Start State Specific Questions',
    viewApplication: 'View Application',
    viewApplicationHelperTextEnabled: 'View Application',
    viewApplicationHelperTextDisabled: 'Application not available',
    viewApplicationButton: 'View',
    loadingText: 'Loading...',
    messageCreateApplicationError: 'Error creating application. Please try again.',
    messageNoCycleFound: 'No application cycle found. Please contact support.',
    messageFetchApplicationsError:
      'We could not load applications. Please try again, or refresh the page.',
    growthAwardHeader: 'Growth Award',
    growthAwardPendingLabel: 'Growth Award application pending',
    growthAwardStartButton: 'Start Growth Award Application',
    stateQuestionsHeader: 'State Questions',
    stateQuestionsPendingLabel: 'State Questions application pending',
    stateQuestionsStartButton: 'Start State Questions Application',
    exportEmailLabel: 'Enter email to receive exported file',
    exportEmailAlreadyAdded: (email: string) =>
      `'${email}' has already been added to the list of export email recipients.`,
    exportEmailPlaceholder: 'Email Address',
    exportErrorMessage: 'Error exporting applications. Please try again.',
    exportGenerating: 'Generating export...',
    exportGenerateCsv: 'Generate CSV',
    exportIncludeQuestionResponses: 'Include question responses (may increase file size)',
    exportInvalidEmailAddress: (email: string) => `'${email}' is not a valid email address.`,
    exportMaxEmailsWarning:
      'You have reached the maximum number of export emails you can receive. Please wait for your current export to finish before requesting another.',
    exportModalTitle: 'Export Applications',
    exportOptionalParams: 'Optional Parameters',
    exportRequiredParams: 'Required Parameters',
    exportSelectApplicationType: 'Select Application Type',
    exportSelectCounty: 'Select County',
    exportSelectPartner: 'Select Partner',
    exportSelectProgram: 'Select Program',
    exportSelectState: 'Select State',
    exportSuccessMessage: 'Export request successful! You will receive an email shortly.',
    exportTypeGrowthAward: 'Growth Award',
    exportTypeMainApplications: 'Main Applications',
    exportTypeStateQuestions: 'State Questions',
  },
  dataUpload: {
    confirmationModal: {
      header: 'Delete Attachment',
      confirmationMessage:
        'Are you sure you want to delete this attachment? This action cannot be undone.',
      confirmationButtonText: 'Delete',
      successMessage: 'Attachment deleted successfully!',
    },
    errorURL: 'Please submit a valid URL',
    helperSelectFile:
      'Click Select to browse to and choose a file on your computer. Click Save to attach it to your application. Please ensure that your filename is unique. Compressed archive formats, e.g. .zip, are not supported.',
    helperUrl:
      'Please only provide links to public webpages and not a document sharing platform (for example, Google Docs or Dropbox). If a URL, please be sure to include http:// or https:// as part of the URL. Click Save to attach it to your application.',
    labelAddFile: 'Add File',
    labelEventProof: 'Attach Proof of the ',
    labelFile: 'File',
    labelURL: 'URL',
    labelSave: 'Save',
    labelSaving: 'Saving',
    labelSelectFile: 'Select',
    labelUpload: 'Uploading',
    messageDownload: 'Click to Download PDF',
    messageFailed: 'Upload Failed.',
    messageSuccess: 'Upload Successful!',
    placeholderURL: 'Enter a URL',
    errorFileSize: 'File size exceeds the 25MB limit.',
    errorFileTypeUnsupported: 'This file type is not supported. Please upload a non-.zip file.',
    warningDuplicateFileSkipped: 'Duplicate file name detected. Duplicate files were skipped.',
    warningDuplicateUrlExists: 'This URL has already been added.',
    labelTryAgain: 'Try again',
    labelUploadedFile: 'Uploaded File',
    labelSavedUrl: 'Saved URL',
    labelGetPdfSummary: 'Get PDF Summary',
    tooltipClearError: 'Clear error',
    tooltipOpen: 'Open',
    tooltipDownload: 'Download',
    tooltipDelete: 'Delete',
    ariaClearUploadError: 'Clear upload error',
    ariaOpenAttachment: 'Open attachment',
    ariaOpenUrlAttachment: 'Open url attachment',
    ariaDownloadAttachment: 'Download attachment',
  },
  deadlineBanner: {
    deadlineText: 'APPLICATION DEADLINE',
    stateReviewDeadlineText: 'State Review Deadline',
  },
  decimalQuestion: {
    maxValueExceeded: `Your input must be a number between 0 and ${formatNumberDisplayValue(
      maxDecimalValue.toString(),
      'decimal'
    )}.`,
  },
  event: {
    confirmationModal: {
      header: 'Delete Event',
      confirmationMessage:
        'Are you sure you want to delete this event? This action cannot be undone.',
      confirmationButtonText: 'Delete Event',
      successMessage: 'Event deleted successfully!',
    },
    buttonAddEvent: 'Add',
    celebration: 'Celebration',
    dateRangeError: 'Please select a date between {{minDate}} and {{maxDate}}.',
    eventName: 'Event Name',
    helperEvent:
      'To add another event, click the Add button below. To remove an additional event, click the delete button on the event (you must have at least one event).',
    labelName: 'When was your community event celebrated?',
    labelSubType: 'What type of Event?',
    labelSubTypeOther: 'Please describe other type of observance:',
    messageDelete: 'Deleting...',
    messageSaved: 'Event Saved!',
    other: 'Other',
    summary: 'Summarize the',
    tooltipDelete: 'Click to remove this event.',
    treeDay: 'Tree Day',
    type: 'Type',
  },
  header: {
    application: 'APPLICATION',
    dashboard: 'DASHBOARD',
    error: 'Error',
    growthaward: 'GROWTH AWARD',
    inactive: 'Inactive User',
    myaccount: 'Account Information',
    mycontact: 'Contact Information',
    pending: 'PENDING',
    pendingMessage:
      'Your account is awaiting validation. You will be able to proceed once an administrator approves your request.',
    signedInAs: 'Signed in as',
    signup: 'SIGN-UP',
    statequestions: 'STATE QUESTIONS',
    tooltipDashboard: 'Return to Dashboard',
    tooltipLanguage: 'Change Language',
    tooltipHelp: 'Help',
    tooltipMyAccount: 'My Account',
    tooltipSignOut: 'Sign Out',
    tooltipStopImpersonation: 'Stop Impersonation',
    tooltipUserManagement: 'User Management',
    usermanagement: 'USER MANAGEMENT',
    instructionsTitle: 'Instructions',
  },
  integerQuestion: {
    invalidInteger: 'Your input must be between 0 and 99,999,999.',
    labelSum: 'Sum of saplings and seedlings must be less than or equal to total trees planted.',
  },
  multiCheckbox: {
    helper: 'Please select at least one',
  },
  multiCheckbox3: {
    helperMaximum: 'No More Than 3 Choices',
  },
  myaccount: {
    accountSettings: 'Account Settings',
    characterlimit: (charLimit: number) => `${charLimit} character limit`,
    errorHelperText: 'Please fill out all required fields',
    emailNewAccountHelperText:
      'To register a new email address, you will need to create a new account.',
    partnerDetails: (partnerName: string) => `${partnerName} Details`,
    preferredlanguage: 'What is your preferred language?',
    primaryContactHeader: 'Primary Contact Information',
    myAccount: 'My Account',
    titlerequired: 'Title required',
    updatePrimaryContactError: 'Error updating primary contact',
    updatePrimaryContactSuccess: 'Primary contact updated',
    updateUserContactSuccess: 'User contact updated',
    updateUserContactError: 'Error updating user contact',
    tabAccountSettings: 'Account Settings',
    tabPrimaryContact: 'Primary Contact',
    tabShippingAddress: 'Shipping Address',
    shippingContactHeader: 'Shipping Contact Information',
    errorUpdatingShippingContact: 'Error updating shipping contact',
    unsavedChangesTitle: 'Unsaved Changes',
    unsavedChangesBody: 'You have unsaved changes. Do you want to discard them and navigate away?',
    buttonKeepEditing: 'Keep Editing',
    buttonDiscardChanges: 'Discard Changes',
    shippingContactUpdated: 'Shipping contact updated',
  },
  partner: {
    other: 'Other',
  },
  partnerUpdateModal: {
    saveAwards: 'Save Awards',
    saveContacts: 'Save Contacts',
    savePartnerInfo: 'Save Partner Info',
    tabContacts: 'Contacts',
    tabPartnerInfo: 'Partner Info',
    tabYearsAwarded: 'Years Awarded',
    titleEditPartnerDetails: 'Edit Partner Details',
    tooltipError: 'Error',
    tooltipHasUnsavedChanges: 'Has Unsaved Changes',
    tooltipSaved: 'Saved',
    tooltipSaving: 'Saving',
  },
  profileMenu: {
    addAccount: 'Add Account',
    addAccountTooltip:
      'This is for organizations that participate in multiple programs or have multiple locations and need to create separate accounts for each.',
    manageAccount: 'Manage Account',
    myAccount: 'My Account',
    signOut: 'Sign Out',
    admin: 'Admin',
    userManagement: 'User Management',
  },
  percentQuestion: {
    invalidPercent: 'Your input must be a number between 0 and 100.',
  },
  signature: {
    buttonClear: 'Clear',
    messageSaved: 'Signature saved!',
    terms: 'Terms and Conditions',
    tooShortError: 'Signature is too short. Please draw a longer signature.',
  },
  submitConfirmation: {
    title: 'Application Submitted',
    message: 'Thank you! Your application has been submitted and is now pending review.',
    reviewApplication: 'Review Application',
    backToDashboard: 'Back to Dashboard',
  },
  signup: {
    buttonSubmit: 'SUBMIT',
    checkContactShipping: 'Same as Primary Contact Address',
    checkIncludeInDirectory:
      'If my application is approved, I DO NOT wish to have my name and email included on the Tree Cities of the World member resource directory (only available to other recognized Tree Cities of the World} communities).',
    choiceNo: 'No',
    choiceSelect: 'Select',
    choiceYes: 'Yes',
    countrySelect: 'Select Your Country',
    fuzzyMatchContinue: (partnerTerm: string) => `Continue Making New ${partnerTerm}`,
    fuzzyMatchMessage:
      'We found some existing partners with similar names in your state. Did you mean one of these?',
    fuzzyMatchSelect: 'Select',
    fuzzyMatchTitle: 'Similar Partners Found',
    errorMessageEnd: 'Check the values and re-enter if necessary.',
    errorMessageStart:
      'There was a problem with your form. The following questions do not have a valid value:',
    errorPartnerHeader: 'Details',
    errorPrimaryContactHeader: 'Primary Contact Details',
    errorShippingContactHeader: 'Shipping Contact Details',
    headerAddAccount: 'Add Account',
    headerExistingPartnerDetails: 'Select Your City',
    headerLabel: ' Information',
    headerPartnerDetails: 'New City Details',
    headerPrimaryContact: 'Primary Contact Information',
    headerProgram: 'Programs Recognition Portal',
    headerSelectProgram: 'What are you applying for?',
    headerShipping: 'Default Shipping Address',
    headerSocialMedia: 'Social Media Channels for Your ',
    helperCityError: 'City required',
    helperEmailError: 'Email required',
    helperNameError: 'Name required',
    helperShippingCityError: 'Shipping City required',
    helperShippingStreetAddressError: 'Shipping Address required',
    helperShippingZipCodeError: 'Please enter valid Shipping Postal Code',
    helperStreetAddressError: 'Address required',
    helperZipCode: 'Please Enter Numerical Values',
    helperZipCodeError: 'Postal Code required',
    labelAppliedToADF: (partnerTerm: string, programName: string) =>
      `Didn't find your ${partnerTerm} or it's your ${partnerTerm}'s first time applying for ${programName} recognition. Click to start a new ${partnerTerm}.`,
    labelCategory: 'What category best describes your ',
    labelCity: 'City',
    labelGenericEmail: 'Generic Email Address for the ',
    labelName: 'Name',
    labelPhoneNumber: 'Phone Number for',
    labelShippingCity: 'Shipping City',
    labelShippingCountry: 'Shipping Country',
    labelShippingStreetAddress: 'Shipping Address',
    labelShippingStreetAddress2: 'Shipping Address 2',
    labelShippingZipCode: 'Shipping Postal Code',
    labelSubCategoryOther: 'Please Specify:',
    labelTitle: 'Title',
    labelZipCode: 'Postal Code',
    messageSubmissionError: 'Error submitting the intake form.',
    messageSubmissionSuccess: 'Intake form successfully submitted.',
    partnerSelect: 'Select Your ',
    required: 'required',
    selectProgram: 'Select Program',
    partnerNotFoundHelperText: (partnerTerm: string) =>
      `No ${partnerTerm} found - Click the checkbox below to start a new ${partnerTerm}`,
    subHeaderExistingPartnerDetails: (partnerTerm: string, programName: string) =>
      `If your ${partnerTerm} has applied for ${programName} recognition in the past, please search for your ${partnerTerm} using this form. If not found, click the checkbox below to start a new ${partnerTerm}.`,
    subHeaderPartnerDetails:
      'Note: if you will be applying for multiple locations, please fill out the below for one location. You will have the opportunity to add additional locations later in the process.',
    tooltipSubmit: 'Click to submit the intake form.',
  },
  userLoadError: {
    contactUs: 'Contact us',
    message:
      'Please try again. If this keeps happening, contact us and include the reference below.',
    referenceLabel: 'Reference',
    signOut: 'Sign out',
    title: "We couldn't load your account.",
    tryAgain: 'Try again',
  },
  userManagement: {
    removeReviewerAssignmentConfirmationModal: {
      header: 'Delete Reviewer Assignment',
      confirmationMessage:
        'Are you sure you want to remove this reviewer assignment? This action cannot be undone.',
      confirmationButtonText: 'Remove Assignment',
      successMessage: 'Reviewer assignment removed successfully!',
    },
    snackbarError: 'Edit Failed',
    snackbarSuccess: 'Updated Successfully',
  },
};
